import { showToast } from '../../../engine/toast';
import {
  adminCreateFaq, adminUpdateFaq,
  adminCreateLegal, adminUpdateLegal, adminGetLegal,
  adminCreateNews, adminUpdateNews, adminGetNews,
  type CmsFaq, type CmsLegal, type CmsNews,
} from '../admin-api';
import { escapeHtml, INPUT_CLS, BTN_CLS } from '../admin-ui';
import type { ModalHandle } from '../modal';
import type { PaneCtx } from '../panes/types';
import { checkboxField, errorText, textField } from './fields';

export type CmsKind = 'legal' | 'faq' | 'news';
export type CmsRow = CmsLegal | CmsFaq | CmsNews;

const TOOLBAR = [
  ['bold', 'italic', 'underline'],
  [{ header: [2, 3, false] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'link'],
  ['clean'],
];

function richTextSlot(label: string): string {
  return `
    <div class="flex flex-col gap-1">
      <span class="text-[10px] text-text-dim">${escapeHtml(label)}</span>
      <!-- Quill owns everything inside. Nothing re-renders this node while the
           dialog is open; a repaint would detach the editor from its DOM. -->
      <div data-quill class="bg-surface-2 border border-border rounded min-h-40"></div>
    </div>`;
}

function footer(): string {
  return `
    <div class="flex justify-end gap-2 pt-3">
      <button type="button" data-action="close" class="min-h-11 px-4 text-sm text-text-dim hover:text-text touch-manipulation">Cancel</button>
      <button type="submit" class="${BTN_CLS}">Save</button>
    </div>`;
}

function bodyFor(kind: CmsKind, row: CmsRow | null): string {
  if (kind === 'legal') {
    const r = (row ?? {}) as Partial<CmsLegal>;
    return `
      <form id="cms" data-form="cms" class="flex flex-col gap-3">
        ${textField('title', 'Title', r.title ?? '', { required: true })}
        ${textField('slug', 'Slug', r.slug ?? '', { required: true, hint: 'Used in the URL, e.g. "terms" or "privacy".' })}
        ${richTextSlot('Content')}
        ${checkboxField('active', 'Published', r.active ?? true)}
        ${footer()}
      </form>`;
  }
  if (kind === 'faq') {
    const r = (row ?? {}) as Partial<CmsFaq>;
    return `
      <form id="cms" data-form="cms" class="flex flex-col gap-3">
        ${textField('question', 'Question', r.question ?? '', { required: true })}
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">Answer</span>
          <textarea name="answer" rows="6" class="${INPUT_CLS} py-2 min-h-24">${escapeHtml(r.answer ?? '')}</textarea>
        </label>
        ${textField('sortOrder', 'Sort order', String(r.sortOrder ?? 0), { type: 'number' })}
        ${checkboxField('active', 'Visible', r.active ?? true)}
        ${footer()}
      </form>`;
  }
  const r = (row ?? {}) as Partial<CmsNews>;
  return `
    <form id="cms" data-form="cms" class="flex flex-col gap-3">
      ${textField('title', 'Title', r.title ?? '', { required: true })}
      ${textField('slug', 'Slug', r.slug ?? '', { hint: 'Generated from the title when left blank.' })}
      ${textField('tag', 'Tag', r.tag ?? '')}
      ${textField('imageUrl', 'Image URL', r.imageUrl ?? '')}
      ${richTextSlot('Content')}
      ${checkboxField('published', 'Published', r.published ?? false)}
      ${footer()}
    </form>`;
}

/**
 * Create or edit one CMS record.
 *
 * Quill is loaded with a DYNAMIC import for two reasons: it is ~200 KB that
 * only an admin ever needs, and it misbehaves in jsdom (it reaches for
 * Selection/Range APIs), so keeping it out of the static graph keeps the
 * admin tests runnable without stubbing it.
 *
 * The editor lives in a modal rather than in the pane because `paintPane()`
 * assigns `innerHTML` — a pane repaint would rip the editor's container out
 * from under it. A modal body is written once and never repainted.
 */
export function openCmsEditor(ctx: PaneCtx, kind: CmsKind, row: CmsRow | null): void {
  // Holds the editor's HTML. Updated from Quill's text-change; read on submit.
  let html = '';
  let quill: { root: HTMLElement } | null = null;

  const needsEditor = kind !== 'faq';
  const title = `${row ? 'Edit' : 'New'} ${kind === 'legal' ? 'legal page' : kind === 'faq' ? 'FAQ entry' : 'news post'}`;

  const handle: ModalHandle = ctx.openModal({
    title,
    size: 'lg',
    body: bodyFor(kind, row),

    onSubmit(k, form, h) {
      if (k !== 'cms') return;
      const data = new FormData(form);
      const str = (n: string) => String(data.get(n) ?? '').trim();
      const checked = (n: string) => (form.querySelector(`[name="${n}"]`) as HTMLInputElement | null)?.checked ?? false;
      const content = quill ? quill.root.innerHTML : html;

      let body: Record<string, unknown>;
      if (kind === 'legal') {
        body = { title: str('title'), slug: str('slug'), content, active: checked('active') };
        if (!body.title || !body.slug) { h.setError('Title and slug are required.'); return; }
      } else if (kind === 'faq') {
        body = {
          question: str('question'),
          answer: str('answer'),
          sortOrder: Number(str('sortOrder')) || 0,
          active: checked('active'),
        };
        if (!body.question || !body.answer) { h.setError('Question and answer are required.'); return; }
      } else {
        body = {
          title: str('title'),
          content,
          published: checked('published'),
          ...(str('slug') ? { slug: str('slug') } : {}),
          ...(str('tag') ? { tag: str('tag') } : {}),
          ...(str('imageUrl') ? { imageUrl: str('imageUrl') } : {}),
        };
        if (!body.title) { h.setError('A title is required.'); return; }
      }

      h.setBusy(true);
      void (async () => {
        try {
          const id = (row as { id?: string } | null)?.id;
          if (kind === 'legal') id ? await adminUpdateLegal(id, body) : await adminCreateLegal(body);
          else if (kind === 'faq') id ? await adminUpdateFaq(id, body) : await adminCreateFaq(body);
          else id ? await adminUpdateNews(id, body) : await adminCreateNews(body);
          showToast('Saved.', 'success');
          h.close();
          await ctx.reload('cms');
        } catch (err) {
          h.setError(errorText(err, 'Could not save that.'));
          h.setBusy(false);
        }
      })();
    },
  });

  if (!needsEditor) return;

  void (async () => {
    // The list rows for legal and news do not carry `content` (news omits it
    // entirely), so the full record is fetched before seeding the editor —
    // otherwise editing a post would silently blank its body on save.
    let initial = '';
    const id = (row as { id?: string } | null)?.id;
    if (id) {
      try {
        const full = kind === 'legal' ? await adminGetLegal(id) : await adminGetNews(id);
        initial = (full as { content?: string }).content ?? '';
      } catch {
        handle.setError('Could not load the existing content.');
      }
    }
    if (ctx.disposed()) return;

    const host = handle.el.querySelector('[data-quill]') as HTMLElement | null;
    if (!host) return; // closed before the import landed

    try {
      const [{ default: Quill }] = await Promise.all([
        import('quill'),
        import('quill/dist/quill.snow.css'),
      ]);
      if (!handle.el.isConnected) return;
      const q = new Quill(host, { theme: 'snow', modules: { toolbar: TOOLBAR } });
      if (initial) q.clipboard.dangerouslyPasteHTML(initial);
      html = initial;
      q.on('text-change', () => { html = q.root.innerHTML; });
      quill = q as unknown as { root: HTMLElement };
    } catch {
      // Editor unavailable — fall back to a plain textarea so the record is
      // still editable rather than the dialog being dead.
      host.innerHTML = `<textarea name="content" rows="10" class="${INPUT_CLS} py-2 w-full min-h-40">${escapeHtml(initial)}</textarea>`;
      const ta = host.querySelector('textarea') as HTMLTextAreaElement;
      html = initial;
      ta.addEventListener('input', () => { html = ta.value; });
    }
  })();
}
